/**
 * EditTimesheetEntryModal — admin/manager/office edits a single day entry
 * (LAA toggle, hours, travel hours, notes) inside a worker's timesheet.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, X } from "lucide-react";
import { Button, Modal, Input, Textarea } from "../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EditTimesheetEntryModal = ({ timesheetId, entry, entryIndex, onClose, onSaved }) => {
  const [form, setForm] = useState({
    hours: "",
    travel_hours: "",
    laa: false,
    notes: "",
    job_title: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setForm({
      hours: entry.hours ?? "",
      travel_hours: entry.travel_hours ?? "",
      laa: !!(entry.laa || entry.living_away_allowance),
      notes: entry.notes || "",
      job_title: entry.job_title || "",
    });
  }, [entry]);

  if (!entry) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        entry_index: entryIndex,
        hours: form.hours === "" ? null : parseFloat(form.hours),
        travel_hours: form.travel_hours === "" ? null : parseFloat(form.travel_hours),
        laa: form.laa,
        living_away_allowance: form.laa,
        notes: form.notes,
        job_title: form.job_title,
      };
      Object.keys(payload).forEach((k) => { if (payload[k] === undefined) delete payload[k]; });
      await axios.patch(`${API}/timesheets/${timesheetId}/entry`, payload);
      toast.success("Entry updated");
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update entry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Edit ${entry.date || entry.day || "Entry"}`} size="md">
      <div className="space-y-3" data-testid="edit-timesheet-entry-modal">
        <Input
          label="Job Title"
          value={form.job_title}
          onChange={(e) => setForm({ ...form, job_title: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="number"
            step="0.1"
            label="Work Hours"
            value={form.hours}
            onChange={(e) => setForm({ ...form, hours: e.target.value })}
            testId="edit-tse-hours"
          />
          <Input
            type="number"
            step="0.1"
            label="Travel Hours"
            value={form.travel_hours}
            onChange={(e) => setForm({ ...form, travel_hours: e.target.value })}
            testId="edit-tse-travel-hours"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer p-2 bg-amber-50 border-2 border-amber-200 rounded">
          <input
            type="checkbox"
            checked={form.laa}
            onChange={(e) => setForm({ ...form, laa: e.target.checked })}
            data-testid="edit-tse-laa"
            className="w-5 h-5"
          />
          <span className="text-sm font-bold text-amber-900">
            Living Away Allowance (LAA) for this day
          </span>
        </label>
        <Textarea
          label="Notes"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
        />
        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="edit-tse-save">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditTimesheetEntryModal;
